import React, { useEffect, useState, useCallback  } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import axiosInstance from "../axiosConfig";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import DataTable from "react-data-table-component";
import "../assets/CSS/Dashboard.css";

function LotDetails() {
  const { lotId } = useParams();
  const [data, setData] = useState([]);
  const [remarks, setRemarks] = useState({});
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const navigate = useNavigate();

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const fetchMergedStockData = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/get-merged-stock-requests");
      const filtered = response.data.filter((item) => item.LotID === lotId);
      setData(filtered);

      const remarksMap = {};
      filtered.forEach((item) => {
        remarksMap[item.stockRecoID] = {
          remark1: item.remark1 || "",
          remark2: item.remark2 || "",
          responseDate: item.responseDate || "",
          request_status: item.request_status || "",
          requestID: item.requestID || null,
        };
      });
      setRemarks(remarksMap);
    } catch (err) {
      console.error("❌ Error fetching merged stock data:", err);
    }
  }, [lotId]);

  useEffect(() => {
    fetchMergedStockData();
  }, [fetchMergedStockData]);

  const handleRemarksBlur = (stockRecoID, value) => {
    const trimmed = value.trim() || null;
    if (trimmed === data.find((r) => r.stockRecoID === stockRecoID)?.remark1) return;

    clearTimeout(window.remarkTimeout);
    window.remarkTimeout = setTimeout(async () => {
      const requestID = remarks[stockRecoID]?.requestID;
      if (!requestID) {
        showAlert("⚠️ Cannot add remark. Please send request first.", "warning");
        return;
      }
      try {
        await axiosInstance.patch(`/api/update-remark/${requestID}`, { remark1: trimmed });
        showAlert("✅ Remark updated.", "success");
        fetchMergedStockData();
      } catch (err) {
        console.error("remark1 update error:", err);
        showAlert("❌ Failed to update remark.", "danger");
      }
    }, 800);
  };

  const sendToStockChangeRequest = async (stockRecoID) => {
    try {
      const response = await axiosInstance.post(`/api/insert-stock-change-request/${stockRecoID}`);

      if (response.data.exists) {
        showAlert("⚠️ This LotID and RMItemCode already exist in request table.", "warning");
      } else {
        showAlert("✅ Request sent successfully.", "success");
      }

      fetchMergedStockData();
    } catch (error) {
        if (error.response?.status === 409) {
        // Already exists (conflict)
        showAlert(error.response.data.message || "⚠️ Already Send  request.", "warning");
        } else if (error.response?.status === 404) {
        showAlert("❌ Record not found for this ID.", "danger");
        } else {
        console.error("Error sending request:", error);
        showAlert("❌ Failed to send the request. Please try again.", "danger");
        }
    }
  };

  const showAlert = (message, type) => {
    const alertPlaceholder = document.getElementById("alertPlaceholder");
    const alertHTML = `
      <div class="alert alert-${type} alert-dismissible fade show" role="alert">
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>`;
    alertPlaceholder.innerHTML = alertHTML;
    setTimeout(() => {
      alertPlaceholder.innerHTML = "";
    }, 2000);
  };

  const columns = [
    {
      name: "Lot ID", width: "180px",
      selector: (row) => row.LotID,
      sortable: true,
    },
    {
      name: "RMItemCode", width: "180px",
      selector: (row) => row.RMItemCode,
      sortable: true,
    },
    {
      name: "AllocateQty",
      selector: (row) => row.AllocateQty,
      sortable: true,
    },
    {
      name: "Consumed",
      selector: (row) => row.quantity_consumed || "N/A",
      sortable: true,
    },
    {
      name: "Received Back",
      selector: (row) => row.quantity_rec_back || "N/A",
      sortable: true,
    },
    {
      name: "RemainingQty",
      selector: (row) => row.RemainingQty,
      sortable: true,
      cell: (row) => {
        const qty = row.RemainingQty;
        if (qty === null || qty === undefined) {
          return <div style={{ textAlign: "center", color: "#999" }}>—</div>;
        }
        const isZero = parseFloat(qty) === 0;
        return (
          <div
            style={{
              backgroundColor: isZero ? "#d4edda" : "#dc3545",
              color: isZero ? "#155724" : "#ffffff",
              padding: "8px 13px",
              borderRadius: "5px",
              textAlign: "center",
              fontWeight: "bold",
              minWidth: "60px",
            }}
          >
            {qty}
          </div>
        );
      },
    },
    {
      name: "Date",
      selector: (row) => row.SyncDate,
      sortable: true,
      cell: (row) => {
        if (!row.SyncDate) return "—";
        const date = new Date(row.SyncDate);
        return date.toISOString().split("T")[0];
      },
    },
    {
      name: "Remark",
      selector: (row) => (
        <span data-bs-toggle="tooltip" data-bs-placement="top" title={remarks[row.stockRecoID]?.remark2}>
          {remarks[row.stockRecoID]?.remark2}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Add Remarks", width: "170px",
      cell: (row) => (
        <textarea
          className="form-control"
          rows={1}
          style={{ fontSize: "12px", width: "150px" }}
          value={remarks[row.stockRecoID]?.remark1 || ""}
          onChange={(e) =>
            setRemarks((prev) => ({
              ...prev,
              [row.stockRecoID]: {
                ...prev[row.stockRecoID],
                remark1: e.target.value,
              },
            }))
          }
          onBlur={(e) => handleRemarksBlur(row.stockRecoID, e.target.value)}
          placeholder="Enter Remark"
        />
      ),
    },
    {
      name: "Status",
      selector: (row) => remarks[row.stockRecoID]?.request_status || "N/A",
      cell: (row) => {
        const status = remarks[row.stockRecoID]?.request_status || "N/A";
        let styles = {
          padding: "4px 12px",
          borderRadius: "6px",
          fontWeight: "bold",
          display: "inline-block",
          fontSize: "11px",
        };
        if (status === "Pending") {
          styles.backgroundColor = "#fff3cd";
          styles.color = "#856404";
        } else if (status === "Rejected") {
          styles.backgroundColor = "#f8d7da";
          styles.color = "#721c24";
        } else if (status === "Approved") {
          styles.backgroundColor = "#d4edda";
          styles.color = "#155747";
        } else {
          styles.backgroundColor = "#e2e3e5";
          styles.color = "#383d41";
        }
        return <span style={styles}>{status}</span>;
      },
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          onClick={() => sendToStockChangeRequest(row.stockRecoID)}
          style={{
            padding: "6px 12px",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Send To
        </button>
      ),
    },
  ];

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>
      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />
        <main className="main-container p-4">
          <div id="alertPlaceholder"></div>
            <button
                className="btn btn-secondary mb-3"
                onClick={() => navigate("/Stock-Reco")}
            >
                ← Back to Stock Reco
            </button>
          <h3 className="mb-4">Lot ID Details: {lotId}</h3>
          <DataTable columns={columns} data={data} pagination highlightOnHover />
        </main>
      </div>
    </div>
  );
}

export default LotDetails;

